import { createTheme } from "@mui/material";
import { getColor } from "../AppTheme";

/**
 * Central design system. Keep it simple + elegant: an airy neutral base,
 * one indigo brand colour, softly rounded cards with a hairline border
 * (instead of heavy shadows), and flat, calm controls. Every page inherits
 * this — avoid per-page colour/shape overrides so the app stays consistent.
 */
export default function initTheme(mode = "light") {
    const isDark = mode === "dark";
    const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.08)";
    const softShadow = isDark
        ? "0 1px 2px rgba(0,0,0,0.4)"
        : "0 1px 2px rgba(16,24,40,0.05)";

    return createTheme({
        mode,
        palette: {
            mode: isDark ? "dark" : "light",
            primary: { main: getColor("primary") },
            secondary: { main: getColor("secondary") },
            success: { main: getColor("success") },
            warning: { main: getColor("warning") },
            error: { main: getColor("error") },
            info: { main: getColor("info") },
            divider: border,
            background: {
                default: getColor("bg"),
                paper: getColor("bg-secondary"),
            },
            text: {
                primary: getColor("text"),
                secondary: getColor("text-secondary"),
            },
        },
        typography: {
            fontFamily: "'Inter', sans-serif",
            h4: { fontWeight: 700, letterSpacing: -0.5 },
            h5: { fontWeight: 700, letterSpacing: -0.3 },
            h6: { fontWeight: 600 },
            subtitle1: { fontWeight: 500 },
            button: { fontWeight: 600 },
        },
        shape: { borderRadius: 12 },
        components: {
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 10,
                        padding: "8px 20px",
                    },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        border: `1px solid ${border}`,
                        boxShadow: softShadow,
                        backgroundImage: "none",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: { borderRadius: 14 },
                    outlined: { borderColor: border },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 8, fontWeight: 600 },
                    sizeSmall: { fontSize: 11 },
                },
            },
            MuiTextField: {
                defaultProps: { variant: "outlined", size: "small" },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: border },
                    head: {
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        color: getColor("text-secondary"),
                        background: isDark
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(17,24,39,0.02)",
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: { root: { borderRadius: 12 } },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: { border: "none", boxShadow: "1px 0 3px rgba(0,0,0,0.04)" },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { borderRadius: 8, fontSize: 12, padding: "6px 10px" },
                },
            },
        },
    });
}
